import { Injectable } from '@angular/core';
import { type DictType } from '@hglobal/utility-kit';

import { OrganizationMembershipRole, OrganizationMembershipStatus } from '../api';
import { type Organization } from '../organizations/organizations.abstraction';
import { RouteFragment } from '../shared/common';
import { UserProfile } from '../shared/services/user/user.abstraction';

import {
  type OrganizationPageAccessMapHandler,
  type OrganizationRouteFragments,
} from './routing.abstraction';

const DEFAULT_ORGANIZATION_ROUTE_FRAGMENTS: readonly OrganizationRouteFragments[] = [
  [RouteFragment.StrategyBoard],
  [RouteFragment.FinanceManagement],
  [RouteFragment.DataAnalytics],
  [RouteFragment.OperationsServices, RouteFragment.Overview],
  [RouteFragment.CompanyDocuments],
];

const MARKETING_READ_ROLES = new Set<DictType<typeof OrganizationMembershipRole>>([
  OrganizationMembershipRole.Owner,
  OrganizationMembershipRole.Manager,
  OrganizationMembershipRole.Member,
  OrganizationMembershipRole.Investor,
]);

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private readonly organizationPageAccessMap = new Map<string, OrganizationPageAccessMapHandler>([
    [
      RouteFragment.StrategyBoard,
      ({ capabilities }) => capabilities.portal.strategyBoard,
    ],
    [
      RouteFragment.FinanceManagement,
      ({ capabilities }) => capabilities.portal.financeManagement,
    ],
    [
      RouteFragment.Marketing,
      (organization) => this.canViewMarketing(organization),
    ],
    [
      RouteFragment.DataAnalytics,
      ({ capabilities }) => capabilities.portal.dataAnalytics,
    ],
    [
      RouteFragment.CompanyDocuments,
      ({ capabilities }) => capabilities.portal.dataUploader,
    ],
    [
      RouteFragment.OperationsServices,
      (organization, childRouteFragment) =>
        this.canAccessOperationsPage(organization, childRouteFragment),
    ],
    [
      RouteFragment.Roles,
      ({ capabilities }) => capabilities.portal.organizationRoles,
    ],
    [
      RouteFragment.EmploymentManagement,
      (organization, childRouteFragment, nestedRouteFragments) =>
        this.canAccessEmploymentManagementPage(organization, childRouteFragment, nestedRouteFragments),
    ],
  ]);

  private readonly operationsSubPagesAccessMap = new Map<string, OrganizationPageAccessMapHandler>([
    [
      RouteFragment.Overview,
      (organization) => this.hasAnyOperationsAccess(organization),
    ],
    [
      RouteFragment.Legal,
      ({ capabilities }) => capabilities.portal.operationsLegal,
    ],
    [
      RouteFragment.Finance,
      ({ capabilities }) => capabilities.portal.operationsFinance,
    ],
    [
      RouteFragment.Hr,
      ({ capabilities }) => capabilities.portal.operationsHr,
    ],
    [
      RouteFragment.It,
      (organization) => this.hasAnyOperationsAccess(organization),
    ],
    [
      RouteFragment.Administrative,
      (organization) => this.hasAnyOperationsAccess(organization),
    ],
  ]);

  canAccessAdminPanel(profile: UserProfile | null | undefined): boolean {
    return Boolean(profile?.permissions.canViewAdminPanel);
  }

  canAccessOrganizationPage(
    organization: Organization | null | undefined,
    routeFragments: OrganizationRouteFragments,
  ): boolean {
    if (!organization) {
      return false;
    }

    const [routeFragment, childRouteFragment, ...nestedRouteFragments] = routeFragments;
    const accessHandler = this.organizationPageAccessMap.get(routeFragment);

    return accessHandler?.(organization, childRouteFragment, nestedRouteFragments) ?? false;
  }

  canCreateOperationsProxyRequest(organization: Organization | null | undefined): boolean {
    return Boolean(organization?.capabilities.portal.canCreateOperationsProxyRequest);
  }

  canManageOrganizationRoles(organization: Organization | null | undefined): boolean {
    if (!organization) {
      return false;
    }

    const canGrantRoles = organization.capabilities.portal.grantRoles;
    const isActiveMember = organization.membership?.status === OrganizationMembershipStatus.Active;

    return canGrantRoles && isActiveMember;
  }

  getDefaultOrganizationRouteFragments(
    organization: Organization | null | undefined,
  ): string[] {
    return [...(DEFAULT_ORGANIZATION_ROUTE_FRAGMENTS.find((routeFragments) =>
      this.canAccessOrganizationPage(organization, routeFragments),
    ) ?? [])];
  }

  getGrantableOrganizationRoles(
    organization: Organization | null | undefined,
  ): Organization['capabilities']['grantableRoles'] {
    return organization?.capabilities.grantableRoles ?? [];
  }

  hasAnyOperationsAccess(organization: Organization | null | undefined): boolean {
    const portal = organization?.capabilities.portal;

    return portal?.operationsLegal === true
      || portal?.operationsFinance === true
      || portal?.operationsHr === true;
  }

  private canAccessEmploymentManagementPage(
    organization: Organization,
    childRouteFragment?: string,
    nestedRouteFragments: readonly string[] = [],
  ): boolean {
    const hasEmploymentManagementAccess =
      organization.capabilities.portal.employmentManagement === true;

    if (!hasEmploymentManagementAccess) {
      return false;
    }

    if (!childRouteFragment) {
      return true;
    }

    if (childRouteFragment === RouteFragment.Payroll) {
      const isPayrollDetailRoute = nestedRouteFragments.length > 0;

      if (isPayrollDetailRoute) {
        return organization.capabilities.portal.canReadPayroll === true;
      }

      return organization.capabilities.portal.canReadPayrolls === true;
    }

    if (childRouteFragment === RouteFragment.People) {
      return true;
    }

    return false;
  }

  private canAccessOperationsPage(
    organization: Organization,
    childRouteFragment: string = RouteFragment.Overview,
  ): boolean {
    const accessHandler = this.operationsSubPagesAccessMap.get(childRouteFragment);

    return accessHandler?.(organization) ?? false;
  }

  private canViewMarketing(organization: Organization): boolean {
    return !organization.membership || this.hasActiveMembershipRole(organization, MARKETING_READ_ROLES);
  }

  private hasActiveMembershipRole(
    organization: Organization,
    roles: ReadonlySet<DictType<typeof OrganizationMembershipRole>>,
  ): boolean {
    const membership = organization.membership;

    return membership?.status === OrganizationMembershipStatus.Active && roles.has(membership.role);
  }
}
